/**
 * The intake pack a therapist fills in when a patient comes to the clinic: the evaluation
 * sheet, the treatment consent, and -- the first time only -- the GDPR agreement. On paper
 * these are three documents; here they are one submission, because they are signed together.
 *
 * The agreement is the part that is not always there. It is given once per patient per
 * clinic and stored against the patient, so a returning patient's intake is a sheet and a
 * treatment consent. The two consents are separate decisions with separate lifetimes:
 * agreeing to be treated names the course of treatment starting today, and agreeing to how
 * the data is processed is about the relationship.
 */
import { IClinicMember } from './clinic';
import { Stroke } from '../bodyDiagramHelper';

/** Who is consenting: the patient, or the adult signing for a child. */
export type ConsentCapacity = 'PATIENT' | 'LEGAL_REPRESENTATIVE';

/**
 * What the wizard holds while it is being filled in.
 *
 * Everything is a string, including the numbers, because that is what an `<input>`
 * gives back and coercing per keystroke is what makes a field impossible to clear.
 * `toCreateEvaluationRequest` does the conversion once, on submit.
 */
export interface EvaluationFormValues {
    // Step 1 -- the patient and the assessment.
    emailAddress: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    occupation: string;
    age: string;
    sex: string;
    evaluatedOn: string;
    reasonForVisit: string;
    investigations: string;
    painLevel: string;
    painCharacter: string;
    medication: string;
    injuryHistory: string;
    notes: string;
    /**
     * What the therapist drew on the body diagram, as strokes in the artwork's own pixels
     * (see `bodyDiagramHelper`). Not a string, unlike everything around it: it is not typed
     * into an input, and keeping the strokes rather than a picture is what lets Undo reach
     * back past closing the dialog.
     */
    bodyDiagram: Stroke[];

    // Step 2 -- consent to treatment, and the signature that goes with it.
    consentCapacity: ConsentCapacity;
    childAge: string;
    patientAddress: string;
    idSeries: string;
    idNumber: string;
    referringDoctor: string;
    treatmentConsentGiven: boolean;
    /**
     * The printed name and the drawing, both required on every sheet: it is the treatment
     * consent they sign, and that one is given again at every visit. They sit here rather
     * than with the agreement, which a returning patient never sees.
     */
    signedName: string;
    /** The drawn signature, as bare base64 of a PNG. Empty until the patient signs. */
    signature: string;

    // Step 3 -- the data-processing agreement, shown only when there is none on file.
    gdprConsentGiven: boolean;
}

/** What POST /evaluations takes. Blank optional fields are dropped rather than sent as "". */
export interface CreateEvaluationRequest {
    emailAddress: string;
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    occupation?: string;
    age?: number;
    sex?: string;
    evaluatedOn?: string;
    reasonForVisit?: string;
    investigations?: string;
    painLevel?: number;
    painCharacter?: string;
    medication?: string;
    injuryHistory?: string;
    notes?: string;
    consentCapacity: ConsentCapacity;
    childAge?: number;
    patientAddress?: string;
    idSeries?: string;
    idNumber?: string;
    referringDoctor?: string;
    treatmentConsentGiven: boolean;
    /**
     * Sent as `false` by a returning patient, whose therapist was never shown the agreement.
     * The API requires it only from somebody with none on file, and refuses the intake
     * otherwise -- so a wrong answer here is a 400 rather than a stored record.
     */
    gdprConsentGiven: boolean;
    signedName?: string;
    /**
     * The handwritten signature, base64 of a PNG with no `data:` prefix. The API renders it
     * into the document and keeps no copy, so it is write-only -- nothing reads it back and
     * `IEvaluation` has no matching field.
     */
    signatureImage?: string;
    /**
     * What was drawn on the body diagram: base64 of a transparent 800x1013 PNG holding the
     * ink alone, with no `data:` prefix. Absent when nothing was drawn. Write-only like the
     * signature: the API composes it onto the outline in the PDF and keeps no copy.
     */
    bodyDiagramImage?: string;
}

/**
 * A patient's data-processing agreement at this clinic. Absent means they have not given
 * one, which is the ordinary state of somebody an admin invited and nobody has evaluated --
 * `GET /evaluations/gdpr-consent` answers 204 for it rather than failing.
 */
export interface IGdprConsent {
    consentedAt: string;
    signedName?: string;
}

/** One line of a patient's history. */
export interface IEvaluationSummary {
    id: number;
    patientUserId: number;
    evaluatedOn: string;
    patientFirstName: string;
    patientLastName: string;
    reasonForVisit?: string;
    painLevel?: number;
    pdfAvailable: boolean;
}

/**
 * A stored evaluation, read back. The names are the ones recorded on the day rather
 * than whatever the account says now, which is the point of keeping them.
 */
export interface IEvaluation extends IEvaluationSummary {
    therapistUserId: number;
    patientPhone?: string;
    occupation?: string;
    age?: number;
    sex?: string;
    investigations?: string;
    painCharacter?: string;
    medication?: string;
    injuryHistory?: string;
    notes?: string;
    consentCapacity: ConsentCapacity;
    childAge?: number;
    patientAddress?: string;
    idSeries?: string;
    idNumber?: string;
    treatmentDescription?: string;
    referringDoctor?: string;
    treatmentConsentAt?: string;
    /** The legible name under the treatment consent's signature, typed on the day. */
    signedName?: string;
    createdAt: string;
}

/** Blank means "not asked", which is not the same as an empty string on the record. */
const trimmed = (value: string): string | undefined => value.trim() || undefined;

/**
 * A stored number, back in the form's own currency. The inverse of `numeric`: a field
 * nobody filled in is a blank input, never the string "undefined" or a nought nobody typed.
 *
 * `null` is checked as well as `undefined` because the type describes the DTO and the wire
 * carries JSON, where an absent number arrives as null however the interface spells it.
 */
const text = (value: number | undefined): string =>
    value === undefined || value === null ? '' : String(value);

/**
 * Blank means absent, and so does anything that is not a number. The API validates the
 * ranges; this only has to avoid sending NaN, which serialises to null and reads on the
 * server as a deliberately cleared field.
 */
const numeric = (value: string): number | undefined => {
    const parsed = Number(value.trim());

    return value.trim() === '' || Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * @param bodyDiagramImage the drawing already exported by `exportBodyDiagram` -- taken as an
 *        argument so this stays a pure mapping with no canvas in it. Empty for none.
 */
export const toCreateEvaluationRequest = (
    values: EvaluationFormValues,
    bodyDiagramImage: string = ''
): CreateEvaluationRequest => ({
    emailAddress: values.emailAddress.trim(),
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    phoneNumber: trimmed(values.phoneNumber),
    occupation: trimmed(values.occupation),
    age: numeric(values.age),
    sex: trimmed(values.sex),
    evaluatedOn: trimmed(values.evaluatedOn),
    reasonForVisit: trimmed(values.reasonForVisit),
    investigations: trimmed(values.investigations),
    painLevel: numeric(values.painLevel),
    painCharacter: trimmed(values.painCharacter),
    medication: trimmed(values.medication),
    injuryHistory: trimmed(values.injuryHistory),
    notes: trimmed(values.notes),
    consentCapacity: values.consentCapacity,
    // Only meaningful for a legal representative, and sending it otherwise would put a
    // child's age on the record of an adult who signed for themselves.
    childAge: values.consentCapacity === 'LEGAL_REPRESENTATIVE'
        ? numeric(values.childAge)
        : undefined,
    patientAddress: trimmed(values.patientAddress),
    idSeries: trimmed(values.idSeries),
    idNumber: trimmed(values.idNumber),
    referringDoctor: trimmed(values.referringDoctor),
    treatmentConsentGiven: values.treatmentConsentGiven,
    gdprConsentGiven: values.gdprConsentGiven,
    signedName: trimmed(values.signedName),
    signatureImage: trimmed(values.signature),
    bodyDiagramImage: trimmed(bodyDiagramImage),
});

/**
 * The wizard, pre-filled for a patient who is already on the list.
 *
 * Split the way a returning patient's paperwork actually splits. What does not change
 * between visits -- who they are, where they live, what is on their ID -- is carried
 * over from the last sheet so nobody retypes an ID card to record a follow-up. What the
 * visit is *about* starts blank, because a fresh assessment is the entire point of
 * recording another one. And the treatment consent is neither carried nor implied: it is
 * given and signed again, on the day, or the sheet is not a consent.
 *
 * The data-processing agreement is the one thing that is not re-asked, and it is not
 * carried here either -- it is simply not this form's to give a second time. It lives
 * against the patient, and `Evaluation` drops the step that collects it when there is one
 * on file. `gdprConsentGiven` therefore stays false: nobody ticked a box today, and saying
 * otherwise would be this form claiming a decision it did not witness.
 *
 * `last` is absent for a patient invited by an admin and never evaluated, which is an
 * ordinary case and not an error -- their identity alone still saves a retype.
 *
 * Takes IClinicMember rather than IPatient: who treats them is irrelevant here, and the
 * narrower type is one this module can name without reaching for the clinic's.
 */
export const toFollowUpFormValues = (
    patient: IClinicMember,
    last: IEvaluation | undefined,
    evaluatedOn: string
): EvaluationFormValues => ({
    emailAddress: patient.emailAddress,
    // The account's name first, the snapshot on the last sheet second: a patient who
    // corrected their name on their profile means the correction, and the older sheet is
    // still the record of what was written on the day.
    firstName: patient.firstName || last?.patientFirstName || '',
    lastName: patient.lastName || last?.patientLastName || '',
    phoneNumber: patient.phoneNumber || last?.patientPhone || '',
    occupation: last?.occupation ?? '',
    age: text(last?.age),
    sex: last?.sex ?? '',
    evaluatedOn,
    reasonForVisit: '',
    investigations: '',
    painLevel: '',
    painCharacter: '',
    medication: '',
    injuryHistory: '',
    notes: '',
    // Blank like the rest of the assessment: the drawing is never stored, and today's
    // picture is the one being recorded.
    bodyDiagram: [],
    consentCapacity: last?.consentCapacity ?? 'PATIENT',
    childAge: text(last?.childAge),
    patientAddress: last?.patientAddress ?? '',
    idSeries: last?.idSeries ?? '',
    idNumber: last?.idNumber ?? '',
    referringDoctor: last?.referringDoctor ?? '',
    treatmentConsentGiven: false,
    signedName: '',
    signature: '',
    gdprConsentGiven: false,
});
